// Type-only import: this module is reachable from AuthProvider, so a runtime
// import here would pull the Alert filter tree (and its data grid / date
// pickers) into the main bundle.
import type { FilterType } from "../Alert/Filter/FilterType";
import { getCurrentLocale } from "../Locale";
import { Module } from "./Module";
import {
  GenericDjangoPaginatedResponse,
  GenericPaginatedResponse,
  GenericResponse,
} from "./utils";

export type Format = "percent" | "currency";

export interface Category {
  id?: number;
  parent_id?: number;
  parent_label?: string;
  name: string;
  label: string;
  type: "multiple" | "date" | "kpi" | "local";
  children?: Array<Category | Dimension>;
}

export interface Dimension {
  id: number;
  label: string;
  granularity: number;
  order: number;
  type: FilterType;
}

export interface DimensionValue {
  id: number;
  label: string;
  name: string;
  order?: number;
  hebergement_nom_court?: string;
  property?: {
    hebergement_nom_court: string;
  };
  uh_type_nandc?: {
    short_name: string;
  } | null;
}

export const checkErrors = (response: Response) => {
  if (!response.ok) {
    if (response.type === "opaqueredirect") {
      throw new Error("Unauthorized");
    }
    // TODO: check error codes
    throw new Error(`${response.status}: ${response.statusText}`);
  }
};

export const checkErrorsFromContent = <T,>(
  json: GenericPaginatedResponse<T> | GenericResponse<T>
) => {
  if (json.responseSuccess !== 1) {
    // TODO: add error message
    throw new Error("API Error");
  }
};

/**
 * Return the default fetch options
 *
 * @param options: the options to override
 */
export const getDefaultOptions = (
  options: RequestInit = {},
  noContentType: boolean = false
): RequestInit => {
  const token = localStorage.getItem("access_token");
  const selectedCustomer = localStorage.getItem("selectedCustomer");

  return {
    method: "GET",
    headers: {
      "Accept-Language": getCurrentLocale(),
      ...(noContentType ? {} : { "Content-Type": "application/json" }),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(selectedCustomer ? { "X-Customer-Name": selectedCustomer } : {}),
      ...options.headers,
    },
    redirect: "manual",
    ...options,
  };
};

interface DimensionAttribute {
  id: number;
  label: string;
  order: number;
  type: FilterType;
  dimension: { granularity: number };
}

const toDimension = (attribute: DimensionAttribute): Dimension => ({
  id: attribute.id,
  label: attribute.label,
  granularity: attribute.dimension.granularity,
  order: attribute.order,
  type: attribute.type,
});

export const listDimensions = async (moduleSlug: string = Module.ALERT) => {
  const url = new URL("/api/dimensions/", window.origin);
  url.searchParams.append("module", moduleSlug);
  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);

  const json: GenericDjangoPaginatedResponse<DimensionAttribute> =
    await response.json();

  return {
    count: json.count,
    results: json.results.map(toDimension),
  };
};

export const getDimension = async (dimensionId: number) => {
  const response = await fetch(
    `/api/dimensions/${dimensionId}/`,
    getDefaultOptions()
  );
  checkErrors(response);
  return toDimension(await response.json());
};

export const listDimensionValues = async (dimensionId: number) => {
  const url = new URL(
    `/api/dimensions/${dimensionId}/values/?page_size=10000`,
    window.origin
  );

  const response = await fetch(url.toString(), getDefaultOptions());

  checkErrors(response);

  const json: GenericDjangoPaginatedResponse<DimensionValue> =
    await response.json();

  return json;
};

export const listHelicopterFilters = async (_moduleSlug: string) => {
  const url = new URL("/api/helicopter/dimensions/", window.origin);
  const response = await fetch(url.toString(), getDefaultOptions());
  checkErrors(response);
  const json = await response.json();
  return {
    count: json.responseCount as number,
    results: (json.responseContent || []).map((f: any) => ({
      id: f.id as number,
      label: f.label as string,
      granularity: f.granularity as number,
      order: f.id as number,
      type: "multiple",
    })),
  };
};

export const listHelicopterFilterValues = async (filterId: number) => {
  const url = new URL(`/api/helicopter/dimensions/${filterId}/`, window.origin);
  const response = await fetch(url.toString(), getDefaultOptions());
  checkErrors(response);
  return response.json();
};
